import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import appConstants from '../../../../../../../../constants';
import { KEYS } from '../../../../../../../../l18n/constants';

import { useL18nContext } from '../../../../../../ContextProvider/L18n';
import { useUserContext } from '../../../../../../ContextProvider/User';
import { useNotificationsContext } from '../../../../../../ContextProvider/Notifications';
import { usePartnerSearchContext } from '../../../../../../ContextProvider/Partner/Search';
import { useNavigationBetweenPartnerFeaturesContext } from '../../../../../../ContextProvider/Navigation/BetweenPartnerFeatures';

import computeTagName from '../../../../../../../../utils/computeTagName';
import computeClassName from '../../../../../../../../utils/computeClassName';
import handleClick from '../../../../../../../../utils/handleClick';
import navigateToURL from '../../../../../../../../utils/navigateToURL';

import CCFormCheckbox from '../../../../../../../libs/CiscoCore/Form/Checkbox';
import PartnerRolesAndLevels from '../../../../../../Partner/RolesAndLevels';
import PartnerSaved from '../../../../../../Partner/Saved';
import PartnerLogo from '../../../../../../Partner/Logo';
import PartnerSpecializationCount from '../../../../../../Partner/SpecializationCount';
import CCBadgeLight from "../../../../../../../libs/CiscoCore/Badge/Light";
import DiversePartnerIndicator from "../../../../../../Partner/DiversePartnerIndicator";


const constants = Object.freeze({
  CLASSNAME_BASE: 'pt-4 sm:pt-6 bg-cc-primary text-cc-primary-contrast shadow-cc-md transform-gpu transition-transform duration-200 ease-in-out hover:scale-105', // hover:ring-1 hover:ring-inset hover:ring-cc-accent
  TAG_DEFAULT: 'article',

  CLASSNAME_FOOTER_BASE: 'p-4 pt-0 sm:p-6 sm:pt-0 transition-colors duration-300 ease-in-out',
  CLASSNAME_FOOTER_CAN_BE_COMPARED: {
    true: {
      IS_COMPARED: {
        true: 'cursor-pointer bg-cc-accent-0 text-cc-accent-60',
        false: 'cursor-pointer hover:bg-cc-accent-0 hover:text-cc-accent'
      }
    },
    false: {
      IS_COMPARED: {
        true: 'cursor-pointer bg-cc-accent-0 text-cc-accent-60',
        false: 'text-cc-muted-contrast bc-cc-gray-0'
      }
    }
  }
});

const propsDefaults = Object.freeze({
  isComparatorToBeShown: false,
  tag: constants.TAG_DEFAULT
});

function getComputedClassName(propsClassName) {
  return computeClassName(propsClassName, constants.CLASSNAME_BASE);
}

function getComputedFooterClassName( canBeCompared = true, isCompared = false) {
  return computeClassName('', `${constants.CLASSNAME_FOOTER_BASE} ${constants.CLASSNAME_FOOTER_CAN_BE_COMPARED[canBeCompared].IS_COMPARED[isCompared]}`);
}

function navigateToPartnerProfile(navigationParams) {
  navigationParams.history.push(`/partner/${navigationParams.partnerId}`);
}

export default function PartnerTile(
  {
    partner,
    selectedPartnerRoleCode,
    isComparatorToBeShown = propsDefaults.isComparatorToBeShown,
    tag = propsDefaults.tag,
    className = ''
  }
) {
  const ComputedTagName = computeTagName(tag, constants.TAG_DEFAULT);
  const computedClassName = getComputedClassName(className);

  const { getTranslationFor } = useL18nContext();
  const translatedSavePartnerActionTitle = getTranslationFor(KEYS.L18N_KEY_LABEL_SAVE);
  const translatedRemovePartnerActionTitle = getTranslationFor(KEYS.L18N_KEY_LABEL_REMOVE);
  const translatedSavingPartnerInProgressText = getTranslationFor(KEYS.L18N_KEY_LABEL_PROCESSING_IN_PROGRESS);

  const { user, userSavedPartnerSiteIds, userSavedPartners } = useUserContext();

  const partnerBeGeoId = partner?.be_geo_id||0;
  const partnerSiteId = partner?.site_id||0;
  const partnerFavTrackId = userSavedPartners.filter(userSavedPartner => userSavedPartner[appConstants.KEY_SITE_ID_LOWERCASE] === partnerSiteId)[0]?.favTrackId||-1;
  const partnerRolesAndLevels = partner?.rolesAndLevels||{};
  const isDiversePartner = partner?.isDiversePartner||false;
  const partnerSavedCount = partner?.savedCount||0;
  const isPartnerSaved = user.isLoggedIn() ? userSavedPartnerSiteIds.indexOf(`${partnerSiteId}`) !== -1 : partner?.isSaved||false;
  const partnerName = partner?.partner_name||'';
  const partnerSpecializations = partner?.qualifications||[]; // those are quals not just specializations... re-work.

  const {
    notifyMustLogInFn,
    notifySuccessfulUserPartnerSavingFn,
    notifyFailedUserPartnerSavingFn,
    notifySuccessfulUserPartnerDeletionFn,
    notifyFailedUserPartnerDeletionFn,
  } = useNotificationsContext();

  const {
    isUserPartnerOperationInProgress,
    saveUserPartner,
    removeUserPartner,
    isUserSavedPartnersFetchingInProgress,
    setRefetchUserSavedPartners
  } = useUserContext();

  const {
    updatePartnerSavedCount,
    addItemToComparator,
    removeItemFromComparator,
    canAddToComparator,
    canActivateComparator,
    getComparedItemComparatorActionLabel,
    getNotComparedItemComparatorActionLabel,
    isItemBeingCompared,
    comparedItemCategory,
    persistAppState
  } = usePartnerSearchContext();

  const [isLocalSavingOperationInProgress, setIsLocalSavingOperationInProgress] = useState(false);

  function beforeLogin() {
    persistAppState();
    navigateToURL(appConstants.SSO_LOGIN_URL);
  }

  const computedSavePartnerButtonClickFn =
    user.isLoggedIn() ?
      () => {
        setIsLocalSavingOperationInProgress(true);
        saveUserPartner(
          user.getId(),
          partnerBeGeoId,
          partnerSiteId,
          () => { updatePartnerSavedCount(partner, 1); notifySuccessfulUserPartnerSavingFn(); setRefetchUserSavedPartners(true); setIsLocalSavingOperationInProgress(false); },
          notifyFailedUserPartnerSavingFn
        );
      } :
      handleClick(notifyMustLogInFn, beforeLogin);

  const history = useHistory();
  const computedPartnerLogoAndNameClickFn = handleClick(navigateToPartnerProfile, { history: history, partnerId: partnerSiteId } );

  const {selectPartnerComparatorFeature} = useNavigationBetweenPartnerFeaturesContext();

  const isPartnerBeingCompared = isComparatorToBeShown ? isItemBeingCompared(partner, appConstants.KEY_SITE_ID_LOWERCASE) : false;
  const canPartnerBeCompared = canAddToComparator();
  const canComparatorBeActivated = canActivateComparator();
  const computedFooterClassName = isComparatorToBeShown ? getComputedFooterClassName(canPartnerBeCompared, isPartnerBeingCompared):'';
  const addOrRemoveFromComparatorClickFn = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if(isPartnerBeingCompared) {
      removeItemFromComparator(partner, appConstants.KEY_SITE_ID_LOWERCASE);
    }
    else {
      if (canPartnerBeCompared) {
        addItemToComparator({...partner}, appConstants.KEY_SITE_ID_LOWERCASE, selectedPartnerRoleCode);
      }
    }
  }
  const addToComparatorOrCompareClickFn = () => isPartnerBeingCompared ? canComparatorBeActivated ? selectPartnerComparatorFeature(): addItemToComparator({...partner}, appConstants.KEY_SITE_ID_LOWERCASE,selectedPartnerRoleCode): canPartnerBeCompared ? addItemToComparator({...partner}, appConstants.KEY_SITE_ID_LOWERCASE,selectedPartnerRoleCode): null;

  const computedRemovePartnerButtonClickFn =
    user.isLoggedIn() ?
      () => {
        if(isPartnerBeingCompared) {
          removeItemFromComparator(partner, appConstants.KEY_SITE_ID_LOWERCASE);
        }
        setIsLocalSavingOperationInProgress(true);
        removeUserPartner(
          user.getId(),
          partnerFavTrackId,
          () => { updatePartnerSavedCount(partner, -1); notifySuccessfulUserPartnerDeletionFn(); setRefetchUserSavedPartners(true); setIsLocalSavingOperationInProgress(false); },
          notifyFailedUserPartnerDeletionFn
        );
      } :
      handleClick(notifyMustLogInFn, beforeLogin);

  return (
    <ComputedTagName className={computedClassName}>
      <header className="px-4 sm:px-6 flex flex-row items-center justify-between">
        <PartnerRolesAndLevels partnerRolesAndLevels={partnerRolesAndLevels}
                               selectedPartnerRoleCode={selectedPartnerRoleCode}/>
        <PartnerSaved savedCount={partnerSavedCount}
                      isSaved = {isPartnerSaved}
                      isActionDisabled = { isLocalSavingOperationInProgress }
                      isSavingInProgress = {isUserPartnerOperationInProgress || isUserSavedPartnersFetchingInProgress}
                      saveActionFn = {computedSavePartnerButtonClickFn}
                      saveActionTitle={translatedSavePartnerActionTitle}
                      removeActionFn = {computedRemovePartnerButtonClickFn}
                      removeActionTitle={translatedRemovePartnerActionTitle}
                      savingInProgressText={translatedSavingPartnerInProgressText}
                      className="pl-4"/>
      </header>
      <div className="cursor-pointer"
           onClick={computedPartnerLogoAndNameClickFn}>
        <div className="p-4 pt-10 sm:px-6 sm:py-5 sm:pt-10" >
          <PartnerLogo partnerBeGeoId={partnerBeGeoId} />
        </div>
        <p className="px-4 mb-2 sm:px-6 font-normal text-cc-100 truncate"
           title={partnerName}
           aria-label={partnerName}>{partnerName}</p>
      </div>
      <DiversePartnerIndicator isDiversePartner={isDiversePartner} className="mb-cc-3.5"/>
      <PartnerSpecializationCount className="px-4 sm:px-6 pb-4 sm:pb-6"
                                  partnerSpecializations={partnerSpecializations}
                                  selectedPartnerRoleCode={selectedPartnerRoleCode}/>
      {
        isComparatorToBeShown && (!comparedItemCategory || (!!comparedItemCategory && (comparedItemCategory === selectedPartnerRoleCode))) &&
        <footer className={computedFooterClassName} onClick={addToComparatorOrCompareClickFn}>
          <div className="pt-4 sm:pt-6 border-t border-cc-gray-05 flex flex-row items-center">
            <span onClickCapture={addOrRemoveFromComparatorClickFn}>
              <CCFormCheckbox isInline={false}
                              isDisabled={!canPartnerBeCompared && !isPartnerBeingCompared}
                              hasError={false}
                              isChecked={isPartnerBeingCompared}
                              size="medium"/>
            </span>
            <span className="ml-3 text-cc-body-small">{isPartnerBeingCompared?getTranslationFor(getComparedItemComparatorActionLabel()):getTranslationFor(getNotComparedItemComparatorActionLabel())}</span>
          </div>
        </footer>
      }
    </ComputedTagName>
  );
}

//{
//  isDiversePartner ?
//    <div className="px-4 sm:px-6 mb-3"><CCBadgeLight size="small">{getTranslationFor(KEYS.L18N_KEY_LABEL_DIVERSE_PARTNER)}</CCBadgeLight></div> :
//    <div className="px-4 sm:px-6 mb-3">&nbsp;</div>
//}
